import { useRef, useState } from "react"
import {
  Trash2, Archive, ChevronLeft, ChevronRight,
  Bold, Italic, Underline, Star, MoreVertical,
  CornerUpLeft, CornerUpRight, AlertOctagon,
  Printer, ExternalLink, SmilePlus,
  Search, X, HelpCircle, Settings, SlidersHorizontal,
} from "lucide-react"
import { formatTime, stripHtml } from "./EmailThreads"

// ── Design tokens ──────────────────────────────────────────────────────────────
const FF   = "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
const MONO = "'IBM Plex Mono', monospace"
const T = {
  border:       "0.5px solid #e5e7eb",
  radius:       8,
  bg:           "#fff",
  text:         "#202124",
  textMuted:    "#3c4043",
  textTertiary: "#5f6368",
  shadow:       "0 1px 3px rgba(0,0,0,0.06)",
}

// ── InterviewsTab-matching avatar gradients ────────────────────────────────────
const AVATAR_GRADIENTS = [
  "linear-gradient(135deg,#6366f1,#8b5cf6)",
  "linear-gradient(135deg,#0ea5e9,#6366f1)",
  "linear-gradient(135deg,#10b981,#0ea5e9)",
  "linear-gradient(135deg,#f59e0b,#ef4444)",
  "linear-gradient(135deg,#ec4899,#8b5cf6)",
  "linear-gradient(135deg,#14b8a6,#6366f1)",
  "linear-gradient(135deg,#f97316,#eab308)",
  "linear-gradient(135deg,#06b6d4,#10b981)",
]
const getAvatarGradient = (name = "") =>
  AVATAR_GRADIENTS[name.split("").reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_GRADIENTS.length]

const getInitials = (name = "") =>
  name.split(" ").filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join("") || "?"

// Avatar — gradient, rounded square (matches InterviewsTab)
export const Avatar = ({ name = "", size = 40 }) => (
  <div style={{
    width: size, height: size, borderRadius: 7,
    background: getAvatarGradient(name),
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0, color: "#fff",
    fontSize: size * 0.38, fontWeight: 700, fontFamily: FF,
    letterSpacing: "0.02em", userSelect: "none",
  }}>
    {getInitials(name)}
  </div>
)

// ── Ghost circle button ────────────────────────────────────────────────────────
const GhostBtn = ({ onClick, title, children, size = 36, disabled = false }) => (
  <button onClick={onClick} title={title} disabled={disabled} style={{
    width: size, height: size, border: "none", borderRadius: "50%",
    background: "transparent", cursor: disabled ? "default" : "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    color: disabled ? "#d1d5db" : T.textTertiary,
    transition: "background 0.12s", outline: "none", flexShrink: 0,
  }}
    onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = "#f1f3f4" }}
    onMouseLeave={e => { e.currentTarget.style.background = "transparent" }}
  >
    {children}
  </button>
)

// ── Reply Editor ───────────────────────────────────────────────────────────────
const ReplyEditor = ({ recipientName, onSend, onDiscard, disabled }) => {
  const ref = useRef(null)
  const [value, setValue] = useState("")
  const [fmts,  setFmts]  = useState({ bold:false, italic:false, underline:false })

  const sync   = () => setFmts({ bold: document.queryCommandState("bold"), italic: document.queryCommandState("italic"), underline: document.queryCommandState("underline") })
  const exec   = cmd => { document.execCommand(cmd, false, null); if (ref.current) setValue(ref.current.innerHTML); sync(); ref.current?.focus() }
  const onInput = () => { if (ref.current) setValue(ref.current.innerHTML); sync() }
  const onKD   = e => {
    if ((e.ctrlKey||e.metaKey) && e.key==="Enter") { e.preventDefault(); onSend(value) }
    if ((e.ctrlKey||e.metaKey) && e.key==="b")     { e.preventDefault(); exec("bold") }
    if ((e.ctrlKey||e.metaKey) && e.key==="i")     { e.preventDefault(); exec("italic") }
    if ((e.ctrlKey||e.metaKey) && e.key==="u")     { e.preventDefault(); exec("underline") }
  }
  const empty = !stripHtml(value).trim()

  return (
    <div style={{ border:"1px solid #c6c6c6", borderRadius:12, boxShadow:"0 2px 6px rgba(0,0,0,0.1)", background:"#fff", overflow:"hidden" }}>
      <style>{`.etv-r[contenteditable][data-ph]:empty:before{content:attr(data-ph);color:#9ca3af;pointer-events:none;font-family:${FF}}.etv-r:focus{outline:none}`}</style>

      {/* To */}
      <div style={{ padding:"10px 16px", borderBottom:T.border, display:"flex", alignItems:"center", gap:8 }}>
        <span style={{ fontSize:12, fontWeight:700, color:T.textTertiary, fontFamily:MONO, textTransform:"uppercase", letterSpacing:"0.07em" }}>To</span>
        <span style={{ fontSize:14, fontWeight:500, color:T.text, fontFamily:FF }}>{recipientName}</span>
      </div>

      {/* Editor */}
      <div ref={ref} contentEditable className="etv-r" data-ph="Reply…"
        onInput={onInput} onKeyDown={onKD} onMouseUp={sync} onKeyUp={sync}
        suppressContentEditableWarning
        style={{ minHeight:120, padding:"14px 16px", fontSize:14, fontFamily:FF, color:T.text, lineHeight:1.7 }}
      />

      {/* Toolbar */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", borderTop:T.border }}>
        <div style={{ display:"flex", gap:2 }}>
          {[{cmd:"bold",Icon:Bold},{cmd:"italic",Icon:Italic},{cmd:"underline",Icon:Underline}].map(({cmd,Icon})=>(
            <button key={cmd} onMouseDown={e=>{e.preventDefault();exec(cmd)}} style={{ width:32,height:32,border:"none",borderRadius:6,cursor:"pointer",background:fmts[cmd]?"#f1f3f4":"transparent",color:fmts[cmd]?T.text:T.textTertiary,display:"flex",alignItems:"center",justifyContent:"center" }}>
              <Icon size={14} strokeWidth={2}/>
            </button>
          ))}
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={onDiscard} style={{ height:34,padding:"0 16px",border:T.border,borderRadius:17,background:"transparent",color:T.textMuted,fontSize:13,fontWeight:600,fontFamily:FF,cursor:"pointer",outline:"none" }}
            onMouseEnter={e=>e.currentTarget.style.background="#f9fafb"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
            Discard
          </button>
          <button disabled={disabled||empty} onClick={()=>onSend(value)} style={{ height:34,padding:"0 24px",border:"none",borderRadius:17,cursor:empty||disabled?"default":"pointer",background:empty||disabled?"#e8eaed":"#0b57d0",color:empty||disabled?"#9aa0a6":"#fff",fontSize:13,fontWeight:600,fontFamily:FF,outline:"none" }}
            onMouseEnter={e=>{ if(!empty&&!disabled) e.currentTarget.style.background="#0842a0" }}
            onMouseLeave={e=>{ if(!empty&&!disabled) e.currentTarget.style.background="#0b57d0" }}>
            Send
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Message card ───────────────────────────────────────────────────────────────
const MessageCard = ({ message, isLast, onReplyClick, isInOrganization }) => {
  const [expanded, setExpanded] = useState(isLast)
  const [starred,  setStarred]  = useState(false)

  const senderIsOrg = message.senderType === "organization"

  const ts = message.timestamp
    ? (message.timestamp instanceof Date ? message.timestamp : new Date(message.timestamp))
    : null

  // "3:59 PM (2 hours ago)" style
  const timeStr = ts ? (() => {
    const time = ts.toLocaleTimeString("en-US", { hour:"numeric", minute:"2-digit", hour12:true })
    const diffMs   = Date.now() - ts.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    let ago = ""
    if      (diffMins < 1)    ago = "just now"
    else if (diffMins < 60)   ago = `${diffMins} minute${diffMins !== 1 ? "s" : ""} ago`
    else if (diffMins < 1440) ago = `${Math.floor(diffMins/60)} hour${Math.floor(diffMins/60) !== 1 ? "s" : ""} ago`
    else                      ago = ts.toLocaleDateString("en-US", { month:"short", day:"numeric", year:"numeric" })
    return diffMins < 1440 ? `${time} (${ago})` : ago
  })() : ""

  return (
    <div style={{ borderBottom: T.border, background:"#fff" }}>
      {/* Row: avatar | content + meta-right */}
      <div
        onClick={() => !expanded && setExpanded(true)}
        style={{ display:"flex", alignItems:"flex-start", padding:"14px 24px", gap:14, cursor: expanded ? "default" : "pointer" }}
      >
        {/* Avatar */}
        <div style={{ flexShrink:0, paddingTop:2 }}>
          <Avatar name={message.senderName || ""} size={40} />
        </div>

        {/* Main column */}
        <div style={{ flex:1, minWidth:0, display:"flex", flexDirection:"column", gap: expanded ? 10 : 0 }}>

          {/* Top row: name+email  |  timestamp + icons */}
          <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:16 }}>

            {/* Left: name + "to" line */}
            <div style={{ minWidth:0, flex:1 }}>
              <div style={{ display:"flex", alignItems:"baseline", gap:6, flexWrap:"wrap" }}>
                <span style={{ fontSize:14, fontWeight:700, color:T.text, fontFamily:FF, whiteSpace:"nowrap" }}>
                  {message.senderName || "Unknown"}
                </span>
                {expanded && message.senderEmail && (
                  <span style={{ fontSize:13, fontWeight:500, color:T.textTertiary, fontFamily:FF }}>
                    &lt;{message.senderEmail}&gt;
                  </span>
                )}
              </div>
              {expanded && (
                <div style={{ fontSize:13, fontWeight:500, color:T.textTertiary, fontFamily:FF, marginTop:1 }}>
                  to {isInOrganization ? (message.recipientName || "Organization") : (message.recipientName || "Candidate")}
                </div>
              )}
            </div>

            {/* Right: timestamp + action icons — fixed width so body never overlaps */}
            <div style={{ display:"flex", alignItems:"center", gap:4, flexShrink:0, paddingTop:1, minWidth: expanded ? 196 : 120 }}>
              {message.isTemp && <span style={{ fontSize:12, color:T.textTertiary, fontFamily:FF }}>Sending…</span>}
              <span style={{ fontSize:13, fontWeight:500, color:T.textTertiary, fontFamily:FF, whiteSpace:"nowrap" }}>
                {timeStr}
              </span>
              {expanded ? (
                <>
                  <GhostBtn title="Star" onClick={e=>{ e.stopPropagation(); setStarred(s=>!s) }} size={32}>
                    <Star size={16} fill={starred?"#f59e0b":"none"} strokeWidth={2.2} color={starred?"#f59e0b":T.textTertiary}/>
                  </GhostBtn>
                  <GhostBtn title="Emoji" size={32}><SmilePlus size={16} strokeWidth={1.8}/></GhostBtn>
                  <GhostBtn title="Reply" onClick={e=>{ e.stopPropagation(); onReplyClick() }} size={32}>
                    <CornerUpLeft size={16} strokeWidth={1.8}/>
                  </GhostBtn>
                  <GhostBtn title="More" size={32}><MoreVertical size={16} strokeWidth={1.8}/></GhostBtn>
                </>
              ) : (
                /* Collapsed: just the star */
                <GhostBtn title="Star" onClick={e=>{ e.stopPropagation(); setStarred(s=>!s) }} size={32}>
                  <Star size={16} fill={starred?"#f59e0b":"none"} strokeWidth={2.2} color={starred?"#f59e0b":T.textTertiary}/>
                </GhostBtn>
              )}
            </div>
          </div>

          {/* Collapsed preview */}
          {!expanded && (
            <p style={{ margin:0, fontSize:13, fontWeight:500, color:T.textTertiary, fontFamily:FF, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:"calc(100% - 32px)" }}>
              {stripHtml(message.text)}
            </p>
          )}

          {/* Expanded body — right col minWidth ensures no overlap with timestamp */}
          {expanded && (
            <div style={{ fontSize:14, fontWeight:500, color:T.text, lineHeight:1.75, fontFamily:FF }}
              dangerouslySetInnerHTML={{ __html: message.text }}
            />
          )}
        </div>
      </div>
    </div>
  )
}

// ── Collapsed count pill ──────────────────────────────────────────────────────
const CollapsedPill = ({ count, onClick }) => (
  <div
    onClick={onClick}
    style={{ display:"flex", alignItems:"center", gap:10, padding:"0 24px 0 24px", height:36, borderBottom:T.border, cursor:"pointer", background:"#fff" }}
    onMouseEnter={e=>e.currentTarget.style.background="#f9fafb"}
    onMouseLeave={e=>e.currentTarget.style.background="#fff"}
  >
    {/* Three dots matching avatar left edge */}
    <div style={{ width:40, display:"flex", alignItems:"center", justifyContent:"center", gap:3, flexShrink:0 }}>
      {[0,1,2].map(i => (
        <div key={i} style={{ width:4, height:4, borderRadius:"50%", background:"#9ca3af" }} />
      ))}
    </div>
    <span style={{ fontSize:13, fontWeight:600, color:"#5f6368", fontFamily:FF }}>
      {count} more {count === 1 ? "message" : "messages"}
    </span>
  </div>
)

// ── EmailThreadView ────────────────────────────────────────────────────────────
const EmailThreadView = ({
  messages,
  metadata,
  loadingMessages,
  sendingMessage,
  showReply,
  setShowReply,
  isInOrganization,
  filteredConversations,
  selectedConversation,
  onBack,
  onSendMessage,
  onSelectConversation,
  messagesEndRef,
  searchQuery = "",
  setSearchQuery,
}) => {
  const [searchFocused,    setSearchFocused]    = useState(false)
  const [subjectStarred,   setSubjectStarred]   = useState(false)
  const [middleExpanded,   setMiddleExpanded]   = useState(false)

  const contactName = isInOrganization ? metadata.candidateName  : metadata.organizationName
  const currentIdx  = filteredConversations.findIndex(c => c.id === selectedConversation?.id)
  const subject     = isInOrganization
    ? `Message from ${metadata.candidateName || "Candidate"}`
    : `Message from ${metadata.organizationName || "Organization"}`

  // Gmail collapse logic: show first + last, clip middle behind pill
  const first   = messages[0]
  const last    = messages[messages.length - 1]
  const middle  = messages.slice(1, -1)
  const hasMany = messages.length > 2

  const ghostBtn = (children, title, onClick) => (
    <button title={title} onClick={onClick} style={{ width:36,height:36,border:"none",borderRadius:"50%",background:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:T.textTertiary,transition:"background 0.12s",outline:"none",flexShrink:0 }}
      onMouseEnter={e=>e.currentTarget.style.background="#f1f3f4"}
      onMouseLeave={e=>e.currentTarget.style.background="transparent"}
    >{children}</button>
  )

  return (
    // Outer: full-height flex column — reply editor sticks to bottom
    <div style={{ display:"flex", flexDirection:"column", height:"100%", background:T.bg }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');
        .etv-search::placeholder{color:#80868b;font-weight:400;}
        @keyframes etv-spin{to{transform:rotate(360deg);}}
      `}</style>

      {/* ── Header (shared with EmailThreads, + Back pill) ───────────── */}
      <div style={{ background:"#fff", borderBottom:"1px solid #e0e0e0", padding:"8px 16px 8px 20px", height:64, display:"flex", alignItems:"center", gap:8, flexShrink:0, boxSizing:"border-box" }}>

        {/* ← Back */}
        <button onClick={onBack} title="Back to Inbox" style={{ display:"flex",alignItems:"center",gap:6,height:36,padding:"0 14px 0 10px",border:T.border,borderRadius:18,background:"transparent",color:T.textMuted,fontSize:13,fontWeight:600,fontFamily:FF,cursor:"pointer",outline:"none",flexShrink:0,transition:"background 0.12s, border-color 0.12s" }}
          onMouseEnter={e=>{ e.currentTarget.style.background="#f1f3f4"; e.currentTarget.style.borderColor="#9ca3af" }}
          onMouseLeave={e=>{ e.currentTarget.style.background="transparent"; e.currentTarget.style.borderColor="#e5e7eb" }}>
          <ChevronLeft size={16} strokeWidth={2}/> Back
        </button>

        {/* Search pill */}
        <div style={{ flex:"0 1 720px", position:"relative" }}>
          <div style={{ position:"absolute",left:16,top:"50%",transform:"translateY(-50%)",pointerEvents:"none",zIndex:1 }}>
            <Search size={18} color={T.textTertiary} strokeWidth={2}/>
          </div>
          <input className="etv-search" type="text" placeholder="Search mail" value={searchQuery}
            onChange={e=>setSearchQuery?.(e.target.value)}
            onFocus={()=>setSearchFocused(true)} onBlur={()=>setSearchFocused(false)}
            style={{ width:"100%",height:46,paddingLeft:48,paddingRight:searchQuery?96:56,border:"none",borderRadius:23,background:searchFocused?"#fff":"rgb(243,243,243)",boxShadow:searchFocused?"0 1px 3px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.08)":"none",fontSize:16,fontFamily:FF,fontWeight:400,color:"#202124",outline:"none",boxSizing:"border-box",transition:"background 0.15s, box-shadow 0.15s" }}
          />
          <div style={{ position:"absolute",right:searchQuery?40:12,top:"50%",transform:"translateY(-50%)" }}>
            <button style={{ width:32,height:32,border:"none",borderRadius:"50%",background:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:T.textTertiary,outline:"none" }}
              onMouseEnter={e=>e.currentTarget.style.background="rgba(0,0,0,0.06)"}
              onMouseLeave={e=>e.currentTarget.style.background="transparent"} title="Search options">
              <SlidersHorizontal size={16} strokeWidth={1.8}/>
            </button>
          </div>
          {searchQuery && <button onClick={()=>setSearchQuery?.("")} style={{ position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",color:T.textTertiary,display:"flex",alignItems:"center",padding:2,outline:"none" }}><X size={14} strokeWidth={2}/></button>}
        </div>

        <div style={{ flex:1 }}/>

        {/* Right cluster */}
        <div style={{ display:"flex",alignItems:"center",gap:2,flexShrink:0 }}>
          {ghostBtn(<HelpCircle size={20} strokeWidth={1.6}/>, "Help")}
          {ghostBtn(<Settings   size={20} strokeWidth={1.6}/>, "Settings")}
          <button style={{ height:36,padding:"0 20px",border:"none",borderRadius:18,background:"#c2e7ff",color:"#001d35",fontSize:14,fontWeight:600,fontFamily:FF,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0,outline:"none",letterSpacing:"-0.01em" }}
            onMouseEnter={e=>e.currentTarget.style.background="#a8d8f8"}
            onMouseLeave={e=>e.currentTarget.style.background="#c2e7ff"}>Upgrade</button>
          {ghostBtn(<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C12 2 12.8 7.2 15.2 9.6C17.6 12 22 12 22 12C22 12 17.6 12 15.2 14.4C12.8 16.8 12 22 12 22C12 22 11.2 16.8 8.8 14.4C6.4 12 2 12 2 12C2 12 6.4 12 8.8 9.6C11.2 7.2 12 2 12 2Z"/></svg>, "Gemini")}
          {ghostBtn(<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><circle cx="5" cy="5" r="1.8" fill="currentColor"/><circle cx="12" cy="5" r="1.8" fill="currentColor"/><circle cx="19" cy="5" r="1.8" fill="currentColor"/><circle cx="5" cy="12" r="1.8" fill="currentColor"/><circle cx="12" cy="12" r="1.8" fill="currentColor"/><circle cx="19" cy="12" r="1.8" fill="currentColor"/><circle cx="5" cy="19" r="1.8" fill="currentColor"/><circle cx="12" cy="19" r="1.8" fill="currentColor"/><circle cx="19" cy="19" r="1.8" fill="currentColor"/></svg>, "Apps")}
        </div>
      </div>

      {/* ── Thread toolbar ───────────────────────────────────────────── */}
      <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",padding:"4px 8px",borderBottom:T.border,background:T.bg,flexShrink:0 }}>
        <div style={{ display:"flex",alignItems:"center",gap:2 }}>
          <div style={{ width:1,height:20,background:"#e5e7eb",margin:"0 4px" }}/>
          {[{Icon:Archive,title:"Archive"},{Icon:AlertOctagon,title:"Report spam"},{Icon:Trash2,title:"Delete"}].map(({Icon,title})=>(
            <GhostBtn key={title} title={title} size={36}><Icon size={18} strokeWidth={1.7}/></GhostBtn>
          ))}
          <div style={{ width:1,height:20,background:"#e5e7eb",margin:"0 4px" }}/>
          <GhostBtn title="More" size={36}><MoreVertical size={18} strokeWidth={1.8}/></GhostBtn>
        </div>
        <div style={{ display:"flex",alignItems:"center",gap:2 }}>
          <span style={{ fontSize:13,fontWeight:600,color:T.textTertiary,fontFamily:FF,padding:"0 4px",whiteSpace:"nowrap" }}>
            {currentIdx + 1} of {filteredConversations.length}
          </span>
          <GhostBtn title="Newer" disabled={currentIdx<=0} onClick={()=>onSelectConversation(filteredConversations[currentIdx-1])} size={32}>
            <ChevronLeft size={16} strokeWidth={2}/>
          </GhostBtn>
          <GhostBtn title="Older" disabled={currentIdx>=filteredConversations.length-1} onClick={()=>onSelectConversation(filteredConversations[currentIdx+1])} size={32}>
            <ChevronRight size={16} strokeWidth={2}/>
          </GhostBtn>
        </div>
      </div>

      {/* ── Scrollable content area ──────────────────────────────────── */}
      <div style={{ flex:1, overflowY:"auto", display:"flex", flexDirection:"column" }}>

        {/* Subject */}
        <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",padding:"20px 24px 12px",flexShrink:0 }}>
          <div style={{ display:"flex",alignItems:"center",gap:10,minWidth:0 }}>
            <h1 style={{ margin:0,fontSize:22,fontWeight:500,color:T.text,fontFamily:FF,letterSpacing:"-0.01em",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>
              {subject}
            </h1>
            <button onClick={()=>setSubjectStarred(s=>!s)} style={{ width:30,height:30,border:"none",borderRadius:"50%",background:"transparent",cursor:"pointer",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",outline:"none" }}
              onMouseEnter={e=>e.currentTarget.style.background="#f1f3f4"}
              onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <Star size={18} fill={subjectStarred?"#f59e0b":"none"} strokeWidth={2.2} color={subjectStarred?"#f59e0b":T.textTertiary}/>
            </button>
          </div>
          <div style={{ display:"flex",gap:2,flexShrink:0 }}>
            <GhostBtn title="Print all" size={32}><Printer size={16} strokeWidth={1.8}/></GhostBtn>
            <GhostBtn title="Open in new window" size={32}><ExternalLink size={16} strokeWidth={1.8}/></GhostBtn>
          </div>
        </div>

        {/* Messages */}
        {loadingMessages ? (
          <div style={{ padding:48,display:"flex",justifyContent:"center" }}>
            <div style={{ width:24,height:24,border:"2px solid #e8eaed",borderTop:"2px solid #0b57d0",borderRadius:"50%",animation:"etv-spin 0.8s linear infinite" }}/>
          </div>
        ) : (
          <>
            {/* First message (if more than 1) */}
            {messages.length > 0 && (
              <MessageCard
                key={first.id}
                message={first}
                isLast={messages.length === 1}
                onReplyClick={()=>setShowReply(true)}
                isInOrganization={isInOrganization}
              />
            )}

            {/* Collapsed middle messages pill */}
            {hasMany && !middleExpanded && middle.length > 0 && (
              <CollapsedPill count={middle.length} onClick={()=>setMiddleExpanded(true)} />
            )}

            {/* Expanded middle messages */}
            {hasMany && middleExpanded && middle.map(msg => (
              <MessageCard
                key={msg.id}
                message={msg}
                isLast={false}
                onReplyClick={()=>setShowReply(true)}
                isInOrganization={isInOrganization}
              />
            ))}

            {/* Last message (always shown) */}
            {messages.length > 1 && (
              <MessageCard
                key={last.id}
                message={last}
                isLast={true}
                onReplyClick={()=>setShowReply(true)}
                isInOrganization={isInOrganization}
              />
            )}
          </>
        )}

        {/* Reply / Forward pill buttons */}
        {!showReply && !loadingMessages && messages.length > 0 && (
          <div style={{ display:"flex",gap:10,padding:"20px 24px 32px 78px" }}>
            {[{Icon:CornerUpLeft,label:"Reply",action:()=>setShowReply(true)},{Icon:CornerUpRight,label:"Forward",action:undefined}].map(({Icon,label,action})=>(
              <button key={label} onClick={action} style={{ display:"flex",alignItems:"center",gap:8,height:36,padding:"0 20px",border:"1px solid #dadce0",borderRadius:18,background:"#fff",cursor:"pointer",fontSize:14,fontWeight:600,fontFamily:FF,color:T.text,outline:"none",transition:"background 0.1s, border-color 0.1s" }}
                onMouseEnter={e=>{ e.currentTarget.style.background="#f6f8fe"; e.currentTarget.style.borderColor="#c5cae9" }}
                onMouseLeave={e=>{ e.currentTarget.style.background="#fff"; e.currentTarget.style.borderColor="#dadce0" }}>
                <Icon size={14} strokeWidth={2}/>{label}
              </button>
            ))}
          </div>
        )}

        <div ref={messagesEndRef}/>
      </div>

      {/* ── Reply editor — outside scroll area, pushes content up ────── */}
      {showReply && (
        <div style={{ flexShrink:0, padding:"12px 24px 20px", borderTop:T.border, background:T.bg }}>
          <ReplyEditor
            recipientName={contactName || ""}
            onSend={onSendMessage}
            onDiscard={()=>setShowReply(false)}
            disabled={sendingMessage}
          />
        </div>
      )}
    </div>
  )
}

export default EmailThreadView